import { Injectable } from '@angular/core';
import {
  PageData,
  ApiBaseResponse,
  SpendHistory,
  Bidder,
  LotBid,
  BulkBidSubmissionResult,
} from '../models';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { map } from 'rxjs/operators';
import { AdminLotData } from '../models/adminLotData';
import { BidderInfo } from '../models/bidderInfo';
import { UserBid } from '../models/userBid';
import { AdminBid } from '../models/adminBId';

@Injectable({
  providedIn: 'root',
})
export class BidService {
  constructor(private http: HttpClient) {}

  getUserSpendingHistory(model: any): Observable<PageData<SpendHistory>> {
    return this.http
      .post<ApiBaseResponse<SpendHistory>>(
        `${environment.apiUrl}/bid/GetBuyerBids`,
        model
      )
      .pipe(
        map((it) => {
          if (model.isPaginated) {
            return {
              data: it.data,
              totalCount: it.pagingInfo.totalCount,
            };
          }
          return {
            data: it.data,
            totalCount: null,
          };
        })
      );
  }

  getAuctionMonitoringData(model: any): Observable<PageData<AdminLotData[]>> {
    return this.http
      .post<ApiBaseResponse<AdminLotData[]>>(
        `${environment.apiUrl}/bid/MonitorAuction`,
        model
      )
      .pipe(
        map((it) => {
          if (model.isPaginated) {
            return {
              data: it.data,
              totalCount: it.pagingInfo.totalCount,
            };
          }
          return {
            data: it.data,
            totalCount: null,
          };
        })
      );
  }

  getBidsOnAuction(model: any): Observable<PageData<UserBid[]>> {
    return this.http
      .post<ApiBaseResponse<UserBid[]>>(
        `${environment.apiUrl}/bid/BidsByBiddersOnAuction`,
        model
      )
      .pipe(
        map((it) => {
          if (model.isPaginated) {
            return {
              data: it.data,
              totalCount: it.pagingInfo.totalCount,
            };
          }
          return {
            data: it.data,
            totalCount: null,
          };
        })
      );
  }

  getBidderInfo(auctionId: number, bidNum: string): Observable<BidderInfo> {
    return this.http
      .post<ApiBaseResponse<BidderInfo>>(
        `${environment.apiUrl}/bid/BidderDetailsForAnAuction`,
        {
          auctionId: auctionId,
          bidderNumber: bidNum,
        }
      )
      .pipe(map((it) => it.data));
  }

  getAuctionBidders(model: any): Observable<PageData<Bidder[]>> {
    return this.http
      .post<ApiBaseResponse<Bidder[]>>(
        `${environment.apiUrl}/bid/AuctionBidders`,
        model
      )
      .pipe(
        map((it) => {
          if (model.isPaginated) {
            return {
              data: it.data,
              totalCount: it.pagingInfo.totalCount,
            };
          }
          return {
            data: it.data,
            totalCount: null,
          };
        })
      );
  }

  getBidsOnLot(model: any): Observable<PageData<LotBid[]>> {
    return this.http
      .post<ApiBaseResponse<LotBid[]>>(
        `${environment.apiUrl}/bid/GetBidsOfALot`,
        model
      )
      .pipe(
        map((it) => {
          if (model.isPaginated) {
            return {
              data: it.data,
              totalCount: it.pagingInfo.totalCount,
            };
          }
          return {
            data: it.data,
            totalCount: null,
          };
        })
      );
  }

  validateBidderNumber(bidderNum: string, auctionId: number): Observable<any> {
    return this.http
      .post<ApiBaseResponse<UserBid[]>>(
        `${environment.apiUrl}/bid/ValidateBidder`,
        {
          bidderNumber: bidderNum,
          auctionId: auctionId,
        }
      )
      .pipe(map((it) => it.data));
  }

  bidsByAdmin(model: any): Observable<PageData<AdminBid[]>> {
    return this.http
      .post<ApiBaseResponse<AdminBid[]>>(
        `${environment.apiUrl}/bid/myBids`,
        model
      )
      .pipe(
        map((it) => {
          if (model.isPaginated) {
            return {
              data: it.data,
              totalCount: it.pagingInfo.totalCount,
            };
          }
          return {
            data: it.data,
            totalCount: null,
          };
        })
      );
  }

  updateBid(model): Observable<any> {
    return this.http
      .post<ApiBaseResponse<any>>(`${environment.apiUrl}/bid/updateBid`, model)
      .pipe(map((it) => it.data));
  }

  deleteAllBids(lotId: number, userId: number): Observable<any> {
    return this.http
      .post<ApiBaseResponse<any>>(`${environment.apiUrl}/bid/DeleteAllBids`, {
        lotId: lotId,
        updatedBy: userId,
      })
      .pipe(map((it) => it.data));
  }

  deleteBid(bidId: number, deletedBy: number): Observable<any> {
    return this.http
      .post<ApiBaseResponse<any>>(`${environment.apiUrl}/bid/DeleteBid`, {
        bidId: bidId,
        updatedBy: deletedBy,
      })
      .pipe(map((it) => it.data));
  }

  restoreBid(bidId: number, deletedBy: number): Observable<any> {
    return this.http
      .post<ApiBaseResponse<any>>(
        `${environment.apiUrl}/bid/RestoreDeletedBid`,
        {
          bidId: bidId,
          updatedBy: deletedBy,
        }
      )
      .pipe(map((it) => it.data));
  }

  submitBulkBid(bulkBid: BulkBid): Observable<any> {
    return this.http
      .post<ApiBaseResponse<any>>(
        `${environment.apiUrl}/bid/SubmitMultipleProxyBids`,
        bulkBid
      )
      .pipe(map((it) => it.data));
  }

  submitPublicBulkBid(bulkBid: BulkBid): Observable<BulkBidSubmissionResult[]> {
    return this.http
      .post<ApiBaseResponse<BulkBidSubmissionResult[]>>(
        `${environment.apiUrl}/bid/SubmitMultipleBids`,
        bulkBid
      )
      .pipe(map((it) => it.data));
  }

  getLotResult(lotId: number): Observable<any> {
    return this.http
      .get<ApiBaseResponse<any[]>>(
        `${environment.apiUrl}/LotResults/LotCurrentStatusByLotId`,
        {
          params: {
            lotId: lotId.toString(),
          },
        }
      )
      .pipe(map((it) => it.data.find((lot) => lot.lotId === lotId)));
  }
}

export interface BulkBid {
  auctionId: number;
  lotAmount: Array<{
    lotId: number;
    amount: number;
  }>;
  userId: number;
  paddleNumber: string;
  proxyUserId: number;
}
